import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import InlineVideoPlayer from "./InlineVideoPlayer";
import { Capacitor } from "@capacitor/core";

// v79.160 — Founder direction (Feb 2026): "Full screen video issue in
// Android app is not resolved serious issue. If unable to fix check
// alternative if we can route to respective source app only or open
// in browser."
//
// Root cause of the fullscreen issue: the in-app `InlineVideoPlayer`
// is a `position:fixed` React overlay, NOT a native fullscreen
// `<video>`. The Capacitor Android status bar (blue "Adukoo" header)
// paints ON TOP of the overlay because we're not in browser
// fullscreen mode — the status bar plugin's `hide()` only kicks in
// on `fullscreenchange`, which never fires for our overlay.
//
// Fix: on the Android Capacitor app ONLY, bypass the in-app player
// entirely and hand the URL off to the OS. Android's native URL
// intent handler (`window.open(url, "_system")` in Capacitor 6) opens
// YouTube in the YouTube app if installed, or Chrome / any default
// browser otherwise — both give true fullscreen with zero Adukoo
// chrome. Browsers + iOS keep the in-app player (works fine there).

/**
 * Global mount point for the InlineVideoPlayer. Any component can
 * dispatch `adukoo:play-media` with `{ url, title }` and this mount
 * will render the modal on top of everything. Same event with an
 * empty payload (or `null` url) closes the modal.
 *
 * Chosen over per-component state because a Funboard card, a listing
 * gallery, a Reels swiper AND a chatbot response might all want to
 * pop the same player — this keeps the logic centralised.
 */
export default function GlobalMediaPlayer() {
    const [payload, setPayload] = useState(null);

    useEffect(() => {
        const handler = (e) => {
            const p = e?.detail || null;
            if (!p || !p.url) { setPayload(null); return; }
            setPayload(p);
        };
        window.addEventListener("adukoo:play-media", handler);
        return () => window.removeEventListener("adukoo:play-media", handler);
    }, []);

    if (!payload) return null;
    return createPortal(
        <InlineVideoPlayer url={payload.url} title={payload.title || ""} onClose={() => setPayload(null)} />,
        document.body
    );
}

/**
 * Convenience helper — dispatches the event.
 *
 * v79.160 — On Android Capacitor, hands URLs off to the OS
 * (native YouTube app / Chrome) instead of the in-app overlay so the
 * user gets true fullscreen with no Capacitor status bar blocking.
 */
export function playMedia(url, title = "") {
    if (!url) return;
    // Native Android: escape to the OS. iOS keeps the in-app player
    // because iOS Safari's WKWebView already gives real fullscreen.
    try {
        if (Capacitor.isNativePlatform && Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android") {
            // Lazy-load @capacitor/browser only if installed; otherwise
            // fall through to `window.open(url, "_system")` which
            // Capacitor's native URL handler intercepts and dispatches
            // via an Android Intent (YouTube app wins if installed,
            // else system browser).
            window.open(url, "_system");
            return;
        }
    } catch { /* on any error fall through to the in-app overlay */ }
    window.dispatchEvent(new CustomEvent("adukoo:play-media", { detail: { url, title } }));
}
